import { LeadTimeComponentOverridePanel } from './LeadTimeComponentOverridePanel';
import { LeadTimeBreakdown } from '../components/LeadTimeBreakdown';
import { useGT } from 'gt-react';
import { Separator } from 'src/components/ui/separator';
import {
  useLeadTimeOverrides,
  useRevokeLeadTimeOverride,
  type LeadTimeOverrideRow,
} from 'src/api/rpc/hooks/procurement/useLeadTimeOverrides';
import { useLeadTimeDetail } from '@/api/rpc/hooks/procurement/useLeadTimeDetail';
import type { GetLeadTimeDetailResponse } from '@/api/rpc/hooks/procurement/useLeadTimeDetail';
import { OverrideCountBadges } from '../components/OverrideCountBadges';
import {
  OverrideRevokeDialog,
  useOverrideRevoke,
} from '../components/OverrideRevoke';
import { LeadTimeOverrideHistory } from './LeadTimeOverrideHistory';
import { LeadTimeOverrideForm } from './LeadTimeOverrideForm';
import { useLeadTimeOverrideForm } from './useLeadTimeOverrideForm';
import {
  activeOverrideCount,
  scheduledOverrideCount,
} from './leadTimeOverrideRows';
import { leadTimeSeriesExtraTargets } from './leadTimeSeriesBulkTargets';

// Manual lead-time override for one SKU x warehouse: what applies right now
// (the pin, or the observed estimate), every override ever set for the series
// with its status, and a form to add one or edit the active one. Editing
// revokes the current row and creates its replacement (the row chain IS the
// history), so an edited row shows as "replaced" rather than changing in
// place. See docs/procurement/lead-time-overrides.md.

// Current effective duration (including configured components), with the raw
// observed estimate retained beside it for measurement/whole-pin provenance.
function EffectiveLeadTime({
  detail,
  scheduledCount,
}: {
  detail: GetLeadTimeDetailResponse | undefined;
  /** Pins whose window has not started; the buyer is told they exist. */
  scheduledCount: number;
}) {
  const gt = useGT();
  if (!detail) return null;
  const { resolved, observed, override, fallbackDays } = detail;
  const effectiveDays =
    detail.currentComposition?.effectiveDays ?? resolved.leadTimeDays;
  const observedText =
    observed.leadTimeDays !== null
      ? gt('Observed PO-to-receipt ~{days}d ({n} receipts)', {
          context: 'lead time override panel observed value',
          days: Math.round(observed.leadTimeDays),
          n: observed.sampleN,
        })
      : gt(
          'No observed PO-to-receipt estimate (Calculated base assumes ~{days}d)',
          {
            context: 'lead time override panel observed value',
            days: fallbackDays,
          },
        );
  return (
    <div className="mb-3 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-gray-500">
          {gt('Current lead time', {
            context: 'lead time override panel',
          })}
        </span>
        <span
          className={override ? 'font-semibold text-blue-700' : 'text-gray-900'}
        >
          {effectiveDays !== null
            ? gt('~{days}d', {
                context: 'lead time override panel effective value',
                days: Math.round(effectiveDays),
              })
            : gt('~{days}d (assumed)', {
                context: 'lead time override panel effective value',
                days: fallbackDays,
              })}
        </span>
      </div>
      <div className="text-right text-gray-400">
        {override
          ? gt('total lead time override · {observed}', {
              context: 'lead time override panel provenance',
              observed: observedText,
            })
          : observedText}
        {scheduledCount > 0
          ? ' · ' +
            gt('{n} future overrides', {
              context:
                'lead time override panel provenance, pins whose window has not started',
              n: scheduledCount,
            })
          : null}
      </div>
    </div>
  );
}

// A pin that has not started yet never applied, so revoking it cancels a plan
// rather than changing today's levels; the dialog says which. `scheduled` comes
// from the history row that requested the revoke, which already judged every
// row against one clock.
function revokeMessage(
  target: LeadTimeOverrideRow | null,
  scheduled: boolean,
  gt: ReturnType<typeof useGT>,
): string | undefined {
  if (!target) return undefined;
  if (scheduled) {
    return gt(
      'The scheduled {days}d total lead time override is cancelled before it starts. The current lead time stays unchanged.',
      {
        context: 'revoke scheduled lead time override confirm message',
        days: target.leadTimeDays,
      },
    );
  }
  return gt(
    'The {days}d total lead time override stops applying now. The next active total lead time override, configured components or existing estimate apply, and policy levels are recomputed.',
    {
      context: 'revoke lead time override confirm message',
      days: target.leadTimeDays,
    },
  );
}

export type BulkLeadTimeHighlight = {
  highlightedLineCount: number;
  series: ReadonlyArray<{ warehouseId: string; productV3Key: string }>;
};

interface LeadTimeOverridePanelProps {
  warehouseId: string;
  /** ProductV3 key (the SKU), authoritative. */
  productV3Key?: string | null;
  bulkLeadTime?: BulkLeadTimeHighlight;
}

// Overrides are ProductV3-only: a line with no SKU has nothing to pin, and
// gating here rather than inside the panel keeps the series identity
// non-nullable for everything below.
export function LeadTimeOverridePanel({
  warehouseId,
  productV3Key,
  bulkLeadTime,
}: LeadTimeOverridePanelProps) {
  const gt = useGT();
  if (!productV3Key) {
    return (
      <p className="text-xs text-gray-400">
        {gt('No lead time to override for this line (no product reference).', {
          context: 'lead time override panel empty state',
        })}
      </p>
    );
  }
  return (
    <SeriesLeadTimePanel
      warehouseId={warehouseId}
      productV3Key={productV3Key}
      bulkLeadTime={bulkLeadTime}
    />
  );
}

function SeriesLeadTimePanel({
  warehouseId,
  productV3Key,
  bulkLeadTime,
}: {
  warehouseId: string;
  productV3Key: string;
  bulkLeadTime?: BulkLeadTimeHighlight;
}) {
  const gt = useGT();
  const identity = { warehouseId, productV3Key };
  const list = useLeadTimeOverrides(identity);
  const detail = useLeadTimeDetail(identity);
  const revokeMutation = useRevokeLeadTimeOverride();
  const extraTargets = leadTimeSeriesExtraTargets(
    bulkLeadTime,
    productV3Key,
    warehouseId,
  );
  const highlightedLineCount = bulkLeadTime?.highlightedLineCount ?? 1;
  const form = useLeadTimeOverrideForm(identity, {
    extraTargets,
    highlightedLineCount,
  });
  const revoke = useOverrideRevoke<LeadTimeOverrideRow>({
    revoke: (o, handlers) =>
      revokeMutation.mutate({ overrideId: o.id }, handlers),
    pending: revokeMutation.isPending,
    successMessage: gt('Total lead time override revoked', {
      context: 'lead time override toast',
    }),
    // The row being edited just stopped existing; drop the edit with it.
    onRevoked: (o) => {
      if (form.editing?.id === o.id) form.reset();
    },
  });

  const overrides = list.data?.overrides ?? [];
  const now = new Date();
  const activeCount = activeOverrideCount(overrides);
  const scheduledCount = scheduledOverrideCount(overrides, now);
  const needsReviewCount = overrides.filter((o) => o.needsReview).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {gt('Lead time', { context: 'lead time override panel heading' })}
        </h4>
        <OverrideCountBadges
          overrideCount={activeCount + scheduledCount}
          needsReviewCount={needsReviewCount}
        />
      </div>

      {detail.data && <LeadTimeBreakdown detail={detail.data} />}
      <LeadTimeComponentOverridePanel
        warehouseId={warehouseId}
        productV3Key={productV3Key}
      />
      <h4 className="border-t pt-2 text-xs font-medium">
        {gt('Total lead time override', {
          context: 'sku warehouse total lead time override section heading',
        })}
      </h4>
      <EffectiveLeadTime detail={detail.data} scheduledCount={scheduledCount} />

      <p className="mb-3 text-xs text-muted-foreground">
        {gt(
          'Total lead time overrides are selected using the calculation date. Future overrides do not apply separately to future demand months.',
          { context: 'lead time calculation date explanation' },
        )}
      </p>

      <LeadTimeOverrideHistory
        overrides={overrides}
        active={list.data?.active ?? null}
        onEdit={form.startEdit}
        onRequestRevoke={revoke.request}
      />

      <Separator className="my-2" />

      <LeadTimeOverrideForm
        form={form}
        highlightedLineCount={highlightedLineCount}
      />

      <p className="mt-1 text-xs text-gray-400">
        {gt(
          'Applies to this item at this warehouse. Safety stock and reorder point are recomputed on save.',
          { context: 'lead time override help text' },
        )}
      </p>

      <OverrideRevokeDialog
        revoke={revoke}
        title={gt('Revoke total lead time override?', {
          context: 'revoke lead time override confirm title',
        })}
        message={revokeMessage(revoke.target, revoke.targetScheduled, gt)}
      />
    </div>
  );
}
